import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  useGetConversationThreadQuery,
  useReplyToMessageMutation,
  useMarkAsReadMutation,
} from 'store/services/message/messageApi';
import MagicButton from 'components/MagicButton/MagicButton';
import { CharacterCounter } from 'components/CharacterCounter/CharacterCounter';
import { EmojiPicker } from 'components/EmojiPicker/EmojiPicker';
import { isSystemMessage, isSystemConversation } from 'utils/messageHelpers';
import './ConversationThreadPage.css';

export default function ConversationThreadPage() {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();

  const [replyMessage, setReplyMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [viewTime, setViewTime] = useState(0);
  const previousMessageCountRef = useRef<number>(0);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, error } = useGetConversationThreadQuery(conversationId!);
  const [replyToMessage, { isLoading: isReplying }] = useReplyToMessageMutation();
  const [markAsRead] = useMarkAsReadMutation();

  // Track scroll
  useEffect(() => {
    const handleScroll = () => setHasScrolled(true);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track view time
  useEffect(() => {
    const timer = setInterval(() => {
      setViewTime(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Mark as read after 2 seconds AND user scrolled
  useEffect(() => {
    if (viewTime >= 2 && hasScrolled && conversationId) {
      markAsRead(conversationId);
    }
  }, [viewTime, hasScrolled, conversationId, markAsRead]);

  // Auto-scroll to bottom only when NEW messages are added (not on initial load)
  useEffect(() => {
    if (data?.messages) {
      const currentMessageCount = data.messages.length;

      // Only auto-scroll if message count increased (new message added)
      if (previousMessageCountRef.current > 0 && currentMessageCount > previousMessageCountRef.current) {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }

      previousMessageCountRef.current = currentMessageCount;
    }
  }, [data?.messages]);


  const handleEmojiSelect = (emoji: string) => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newMessage = replyMessage.substring(0, start) + emoji + replyMessage.substring(end);

    setReplyMessage(newMessage);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + emoji.length, start + emoji.length);
    }, 0);
  };

  const handleReply = async () => {
    if (!replyMessage.trim() || !conversationId) return;

    try {
      await replyToMessage({
        conversationId,
        content: replyMessage.trim(),
      }).unwrap();

      setReplyMessage('');
      toast.success('Reply sent!');
    } catch (error: any) {
      console.error('Failed to send reply:', error);
      // 429 errors are now handled globally in baseQuery
      // Only handle other errors here
      if (error.status !== 429) {
        toast.error(error.data?.message || 'Failed to send reply');
      }
    }
  };

  if (isLoading) {
    return (
      <div className="conversation-page">
        <div className="conversation-loading">Loading conversation...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="conversation-page">
        <div className="conversation-error">
          {(error as any).status === 404
            ? "This conversation doesn't exist or you don't have access."
            : 'Failed to load conversation. Please try again.'}
        </div>
        <button onClick={() => navigate('/inbox')} className="back-to-inbox-button">
          Back to Inbox
        </button>
      </div>
    );
  }

  if (!data) return null;

  const formatMessageTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const isSystemThread = isSystemConversation(data.otherUser.id, data.otherUser.username);

  return (
    <div className="conversation-page">
      <div className="conversation-header">
        <button onClick={() => navigate('/inbox')} className="back-button">
          ← Back to Inbox
        </button>
        <div className="conversation-user-info">
          {data.otherUser.avatarUrl ? (
            <img
              src={data.otherUser.avatarUrl}
              alt={data.otherUser.username}
              className="conversation-avatar"
            />
          ) : (
            <div className="conversation-avatar-placeholder">
              {data.otherUser.username.charAt(0).toUpperCase()}
            </div>
          )}
          <span className="conversation-username">{data.otherUser.username}</span>
        </div>
      </div>

      <div className="conversation-messages">
        {data.messages.map((message) => {
          const isSystem = isSystemMessage(message);
          return (
            <div
              key={message.id}
              className={`message-bubble ${
                isSystem
                  ? 'system-message'
                  : message.sender?.id === data.otherUser.id ? 'received' : 'sent'
              }`}
            >
              <div className="message-sender-info">
                {message.sender?.avatarUrl ? (
                  <img
                    src={message.sender.avatarUrl}
                    alt={message.sender?.username || 'System'}
                    className="message-avatar"
                  />
                ) : (
                  <div className={`message-avatar-placeholder ${isSystem ? 'system-avatar' : ''}`}>
                    {message.sender?.username ? message.sender.username.charAt(0).toUpperCase() : 'S'}
                  </div>
                )}
                <span className="message-sender">{message.sender?.username || 'System'}</span>
                <span className="message-time">{formatMessageTime(message.createdAt)}</span>
              </div>
              <div className="message-content">
                {isSystem ? (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      a: ({ href, children }) => {
                        const isExternal = href?.startsWith('http');
                        const isMailto = href?.startsWith('mailto:');

                        if (isMailto) {
                          return <a href={href}>{children}</a>;
                        }

                        if (isExternal) {
                          return (
                            <a href={href} target="_blank" rel="noopener noreferrer">
                              {children}
                            </a>
                          );
                        }

                        return <Link to={href || '#'}>{children}</Link>;
                      },
                    }}
                  >
                    {message.content}
                  </ReactMarkdown>
                ) : (
                  message.content
                )}
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {!isSystemThread && (
        <div className="conversation-reply-section">
          <div className="reply-form-group">
            <label className="reply-label">Reply</label>
            <textarea
              ref={textareaRef}
              className="reply-textarea"
              placeholder="Type your reply here..."
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              rows={4}
              maxLength={2000}
            />
            <CharacterCounter
              currentLength={replyMessage.length}
              maxLength={2000}
              warningThreshold={1800}
            />
          </div>

          <div className="reply-actions">
            <div className="emoji-picker-container">
              <button
                ref={emojiButtonRef}
                className="emoji-picker-button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                type="button"
              >
                😀
              </button>
              {showEmojiPicker && (
                <EmojiPicker
                  onEmojiSelect={handleEmojiSelect}
                  onClose={() => setShowEmojiPicker(false)}
                  buttonRef={emojiButtonRef}
                />
              )}
            </div>

            <MagicButton
              label={isReplying ? 'Sending...' : 'Send Reply'}
              color="purple"
              onPress={handleReply}
              isDisabled={
                !replyMessage.trim() ||
                replyMessage.length > 2000 ||
                isReplying
              }
              fullWidth
            />
          </div>
        </div>
      )}
    </div>
  );
}
